import { ShowBase, useNotify, useRecordContext, useRefresh } from "ra-core";
import { useEffect, useState } from "react";
import { Button, Tag } from "@carbon/react";
import { Edit } from "@carbon/icons-react";
import { Link, useNavigate } from "react-router";
import { getSupabaseClient } from "../providers/supabase/supabase";
import "./leads.css";
import { useConfigurationContext } from "../root/ConfigurationContext";

type LeadRecord = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  title?: string | null;
  status: string;
  source: string;
  rating: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  industry?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  annual_revenue?: number | string | null;
  currency_code?: string | null;
  description?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  converted_at?: string | null;
  converted_account_id?: number | null;
  converted_contact_id?: number | null;
  converted_deal_id?: number | null;
};

const statusLabels: Record<string, string> = {
  new: "新建", working: "跟进中", qualified: "已确认资格",
  nurture: "持续培育", converted: "已转化", disqualified: "不合格",
};
const sourceLabels: Record<string, string> = {
  website: "官网", referral: "客户推荐", event: "市场活动", outbound: "主动开发",
  partner: "合作伙伴", social: "社交媒体", other: "其他",
};

const LeadDetails = () => {
  const lead = useRecordContext<LeadRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const { dealStages } = useConfigurationContext();
  const [isConverting, setIsConverting] = useState(false);
  const [isConversionOpen, setIsConversionOpen] = useState(false);
  const [accounts, setAccounts] = useState<Array<{ id: number; name: string }>>([]);
  const [accountId, setAccountId] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [createOpportunity, setCreateOpportunity] = useState(false);
  const [opportunityName, setOpportunityName] = useState("");
  const [opportunityAmount, setOpportunityAmount] = useState("0");
  const [opportunityStage, setOpportunityStage] = useState("opportunity");

  useEffect(() => {
    if (!isConversionOpen || !lead) return;
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const { data, error } = await getSupabaseClient().from("companies").select("id,name").order("name").limit(500);
        if (error) throw error;
        if (cancelled) return;
        const rows = (data ?? []) as Array<{ id: number; name: string }>;
        setAccounts(rows);
        const match = rows.find((item) => item.name.trim().toLowerCase() === (lead.company_name ?? "").trim().toLowerCase());
        setAccountId((current) => current || (match ? String(match.id) : ""));
        setLookupError("");
      } catch {
        if (!cancelled) setLookupError("无法读取现有客户列表，请检查权限后重试。");
      }
    };
    void loadAccounts();
    return () => { cancelled = true; };
  }, [isConversionOpen, lead?.id, lead?.company_name]);

  const convertLead = async () => {
    if (!lead || isConverting) return;
    setIsConverting(true);
    try {
      if (createOpportunity && (!opportunityName.trim() || !Number.isFinite(Number(opportunityAmount)) || Number(opportunityAmount) < 0)) {
        notify("请填写有效的商机名称和非负金额", { type: "warning" });
        return;
      }
      const { data, error } = await getSupabaseClient().rpc("orbitas_convert_lead", {
        p_lead_id: lead.id,
        p_existing_account_id: accountId ? Number(accountId) : null,
        p_existing_contact_id: null,
        p_create_opportunity: createOpportunity,
        p_opportunity_name: createOpportunity ? opportunityName.trim() : null,
        p_opportunity_amount: createOpportunity ? Number(opportunityAmount) : 0,
        p_opportunity_stage: opportunityStage,
      });
      if (error) throw error;
      const result = data as { account_id: number; contact_id: number; deal_id?: number | null };
      notify("线索已成功转化，客户与联系人已关联", { type: "success" });
      refresh();
      navigate(result.deal_id ? "/deals/" + result.deal_id + "/show" : "/companies/" + result.account_id + "/show");
    } catch (error) {
      const message = error instanceof Error ? error.message : "转化失败，请稍后重试";
      notify(message, { type: "error", multiLine: true });
    } finally {
      setIsConverting(false);
    }
  };

  if (!lead) return null;
  return <section className="orbitas-leads-page">
    <div className="orbitas-page-heading"><div><p className="orbitas-eyebrow">客户与市场 / 线索</p><h1>{[lead.first_name, lead.last_name].filter(Boolean).join(" ") || "未命名线索"}</h1><p>{lead.company_name || "尚未填写企业名称"}{lead.title ? ` · ${lead.title}` : ""}</p></div>
      <div className="flex flex-wrap items-center gap-2">
        {lead.status === "converted" && lead.converted_account_id ? (
          <>
            <Link to={"/companies/" + lead.converted_account_id + "/show"} className="cds--btn cds--btn--tertiary">查看已转化客户</Link>
            {lead.converted_deal_id ? <Link to={"/deals/" + lead.converted_deal_id + "/show"} className="cds--btn cds--btn--tertiary">查看关联商机</Link> : null}
          </>
        ) : lead.status !== "disqualified" ? (
          <Button kind="primary" size="md" disabled={isConverting} onClick={() => setIsConversionOpen((open) => !open)}>
            {isConversionOpen ? "关闭转化面板" : "转化线索"}
          </Button>
        ) : null}
        <Link to={"/leads/" + lead.id} className="cds--btn cds--btn--tertiary"><Edit size={16} aria-hidden="true" />编辑线索</Link>
      </div>
    </div>
    {isConversionOpen && lead.status !== "converted" ? (
      <section className="orbitas-lead-conversion-form" aria-label="线索转化设置">
        <h2>转化设置</h2>
        <p>可复用现有客户，并按邮箱自动匹配联系人；也可同时创建商机。</p>
        {lookupError ? <p role="alert" className="orbitas-lead-conversion-form__error">{lookupError}</p> : null}
        <label className="cds--form-item"><span className="cds--label">目标客户</span>
          <div className="cds--select"><select className="cds--select-input" value={accountId} onChange={(event) => setAccountId(event.target.value)}>
            <option value="">新建客户：{lead.company_name || "使用线索企业名称"}</option>
            {accounts.map((account) => <option key={account.id} value={String(account.id)}>{account.name} · #{account.id}</option>)}
          </select><svg className="cds--select__arrow" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg></div>
        </label>
        <label className="cds--checkbox-wrapper"><input className="cds--checkbox" type="checkbox" checked={createOpportunity} onChange={(event) => setCreateOpportunity(event.target.checked)} /><span className="cds--checkbox-label">同时创建商机</span></label>
        {createOpportunity ? <div className="orbitas-lead-conversion-form__grid">
          <label className="cds--form-item"><span className="cds--label">商机名称</span><input className="cds--text-input" value={opportunityName} onChange={(event) => setOpportunityName(event.target.value)} placeholder={lead.company_name ? lead.company_name + " - 新商机" : "输入商机名称"} required /></label>
          <label className="cds--form-item"><span className="cds--label">商机金额</span><input className="cds--text-input" type="number" min="0" step="1" value={opportunityAmount} onChange={(event) => setOpportunityAmount(event.target.value)} required /></label>
          <label className="cds--form-item"><span className="cds--label">初始阶段</span><div className="cds--select"><select className="cds--select-input" value={opportunityStage} onChange={(event) => setOpportunityStage(event.target.value)}>{dealStages.map((stage) => <option key={stage.value} value={stage.value}>{stage.label}</option>)}</select><svg className="cds--select__arrow" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg></div></label>
        </div> : null}
        <div className="orbitas-lead-conversion-form__actions"><Button kind="secondary" size="md" disabled={isConverting} onClick={() => setIsConversionOpen(false)}>取消</Button><Button kind="primary" size="md" disabled={isConverting || Boolean(lookupError)} onClick={convertLead}>{isConverting ? "正在转化…" : "确认转化"}</Button></div>
      </section>
    ) : null}
    <div className="orbitas-lead-detail-grid">
      <article><span>线索状态</span><strong><Tag type={lead.status === "qualified" ? "green" : lead.status === "disqualified" ? "red" : "blue"}>{statusLabels[lead.status] ?? lead.status}</Tag></strong></article>
      <article><span>线索来源</span><strong>{sourceLabels[lead.source] ?? lead.source ?? "—"}</strong></article>
      <article><span>评分</span><strong>{lead.rating === "hot" ? "高" : lead.rating === "cold" ? "低" : "中"}</strong></article>
      <article><span>工作邮箱</span><strong>{lead.email || "—"}</strong></article>
      <article><span>联系电话</span><strong>{lead.phone || "—"}</strong></article>
      <article><span>企业网站</span><strong>{lead.website || "—"}</strong></article>
      <article><span>所属行业</span><strong>{lead.industry || "—"}</strong></article>
      <article><span>地区</span><strong>{[lead.city, lead.state, lead.country].filter(Boolean).join(" / ") || "—"}</strong></article>
      <article><span>企业年收入（估算）</span><strong>{lead.annual_revenue == null ? "—" : new Intl.NumberFormat("zh-CN", { style: "currency", currency: lead.currency_code || "USD", maximumFractionDigits: 0 }).format(Number(lead.annual_revenue))}</strong></article>
      <article className="orbitas-lead-description"><span>背景与需求</span><p>{lead.description || "暂无补充信息"}</p></article>
    </div>
    {lead.status === "converted" ? (
      <section className="orbitas-lead-conversion" aria-label="转化结果">
        <strong>已完成转化</strong>
        <p>客户：{lead.converted_account_id ? <Link to={"/companies/" + lead.converted_account_id + "/show"}>#{lead.converted_account_id}</Link> : "—"}　联系人：{lead.converted_contact_id ? <Link to={"/contacts/" + lead.converted_contact_id + "/show"}>#{lead.converted_contact_id}</Link> : "—"}</p>
        <p>转化时间：{lead.converted_at ? new Date(lead.converted_at).toLocaleString("zh-CN") : "—"}</p>
      </section>
    ) : null}
    <p className="orbitas-lead-meta">创建于 {lead.created_at ? new Date(lead.created_at).toLocaleString("zh-CN") : "—"} · 更新于 {lead.updated_at ? new Date(lead.updated_at).toLocaleString("zh-CN") : "—"}</p>
  </section>;
};

export const LeadShow = () => <ShowBase><LeadDetails /></ShowBase>;
